/**
 * Per-frame modulation evaluator.
 *
 * Given a preset's base params + a list of bindings + the current AudioFrame,
 * computes the live (modulated) param bundle. Pure function over its own
 * smoothing state; no global mutation outside the bundle.
 *
 * Multiple bindings on the same target sum together. Final value is:
 *
 *   final[target] = base[target]
 *                 + sum_i ( smooth(transform_i(source_i)) * amount_i + offset_i )
 */

import type { AudioFrame } from '../../audio/analysis/analyser';
import type { ModulationBinding } from '../presets/presetTypes';
import {
  TARGET_GETTERS,
  TARGET_SETTERS,
  type ParamBundle,
} from './targets';
import {
  applyTransform,
  readSource,
  smoothValue,
} from './transforms';

export class ModulationEvaluator {
  private smoothed = new Float32Array(0);
  private bindings: ModulationBinding[] = [];

  /**
   * Adopt a new binding list. Smoothing state is reset (previous values
   * don't carry meaning under a different binding configuration).
   */
  setBindings(bindings: ModulationBinding[]): void {
    this.bindings = bindings;
    this.smoothed = new Float32Array(bindings.length);
  }

  /**
   * Apply modulation to `target` based on `frame` and the bindings most
   * recently passed to `setBindings`.
   *
   * `target` is mutated in place: the engine passes the working bundle that
   * gets uploaded to the GPU each frame. Caller must reset `target` to the
   * preset's *base* values before each call (the easiest way is to copy
   * from the preset's params).
   */
  evaluate(frame: AudioFrame, target: ParamBundle): void {
    for (let i = 0; i < this.bindings.length; i++) {
      const b = this.bindings[i];
      if (!b) continue;
      const raw = readSource(b.source, frame);
      const transformed = applyTransform(b.transform, raw, { rate: b.rate ?? 0 });
      const prev = this.smoothed[i] ?? transformed;
      const smoothMs = b.smoothingMs ?? 0;
      const sm = smoothValue(prev, transformed, smoothMs, frame.dt);
      this.smoothed[i] = sm;
      const contribution = sm * b.amount + (b.offset ?? 0);
      const base = TARGET_GETTERS[b.target](target);
      TARGET_SETTERS[b.target](target, base + contribution);
    }
  }
}
